import { jsPDF } from "jspdf";

const C = {
  primary: [72, 52, 212],
  primaryDark: [45, 30, 145],
  primarySoft: [239, 236, 255],
  accent: [16, 185, 129],
  text: [30, 32, 45],
  textLight: [110, 118, 135],
  textMuted: [155, 162, 178],
  white: [255, 255, 255],
  border: [225, 229, 240],
  borderSoft: [240, 242, 250],
  paper: [252, 252, 255],
};

const PAGE_W = 297;
const PAGE_H = 210;

const QCOLORS = [
  { main: [72, 52, 212], soft: [239, 236, 255] },
  { main: [16, 185, 129], soft: [220, 250, 240] },
  { main: [245, 158, 11], soft: [254, 243, 218] },
  { main: [219, 39, 119], soft: [253, 224, 235] },
];

const loadImageAsBase64 = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({
        data: canvas.toDataURL("image/png"),
        w: img.naturalWidth,
        h: img.naturalHeight,
      });
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const drawBackground = (doc) => {
  doc.setFillColor(...C.paper);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");

  doc.setFillColor(245, 243, 255);
  doc.circle(-15, PAGE_H + 15, 60, "F");
  doc.setFillColor(240, 250, 245);
  doc.circle(PAGE_W + 15, -15, 55, "F");
};

const drawHeader = (doc, logo) => {
  doc.setFillColor(...C.primary);
  doc.rect(0, 0, PAGE_W, 3, "F");

  if (logo) {
    const targetH = 12;
    const targetW = (logo.w / logo.h) * targetH;
    doc.addImage(logo.data, "PNG", 15, 10, targetW, targetH);
  }

  doc.setTextColor(...C.text);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("Mi Rol Vital", PAGE_W / 2, 19, { align: "center" });

  doc.setTextColor(...C.textLight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Resumen de confirmación", PAGE_W / 2, 25, { align: "center" });

  const fecha = new Date().toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  doc.setTextColor(...C.textLight);
  doc.setFontSize(8);
  doc.text(fecha, PAGE_W - 15, 15, { align: "right" });
};

const drawQuadrant = (doc, x, y, w, h, num, pregunta, frases, color) => {
  doc.setFillColor(235, 237, 245);
  doc.roundedRect(x + 0.8, y + 1, w, h, 5, 5, "F");

  doc.setFillColor(...C.white);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.35);
  doc.roundedRect(x, y, w, h, 5, 5, "FD");

  doc.setFillColor(...color.soft);
  doc.roundedRect(x, y, w, 14, 5, 5, "F");
  doc.rect(x, y + 9, w, 5, "F");

  doc.setFillColor(...color.main);
  doc.circle(x + 10, y + 7, 4.6, "F");
  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(String(num), x + 10, y + 8.4, { align: "center" });

  doc.setTextColor(...C.primaryDark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  const qLines = doc.splitTextToSize(pregunta, w - 22);
  doc.text(qLines.slice(0, 2), x + 17, y + 6.5);

  const contentY = y + 18;
  const rowH = 6;
  const maxRows = Math.floor((h - 22) / rowH);
  const visibleFrases = frases.slice(0, maxRows);

  visibleFrases.forEach((item, i) => {
    const rowY = contentY + i * rowH;

    if (i % 2 === 0) {
      doc.setFillColor(...C.borderSoft);
      doc.roundedRect(x + 4, rowY, w - 8, rowH - 1, 1.5, 1.5, "F");
    }

    doc.setFillColor(...color.main);
    doc.circle(x + 7, rowY + 2.8, 1.2, "F");

    doc.setTextColor(...C.text);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    const talentoW = doc.getTextWidth(item.talento || "");
    const fraseText = item.frase || "";
    const fraseLines = doc.splitTextToSize(fraseText, w - 20 - talentoW);
    doc.text(fraseLines[0] || "", x + 11, rowY + 3.4);

    if (item.talento) {
      doc.setTextColor(...color.main);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.text(item.talento, x + w - 5, rowY + 3.4, { align: "right" });
    }
  });

  if (frases.length > maxRows) {
    doc.setTextColor(...C.textMuted);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(6.5);
    doc.text(
      `+${frases.length - maxRows} más...`,
      x + w / 2,
      y + h - 4,
      { align: "center" },
    );
  }
};

const drawFooter = (doc) => {
  const y = PAGE_H - 12;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(15, y - 4, PAGE_W - 15, y - 4);

  doc.setTextColor(...C.textLight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Universidad del Talento", 15, y);

  doc.setFont("helvetica", "italic");
  doc.text("Descubre tu talento", PAGE_W - 15, y, { align: "right" });
};

export const generateConfirmacionPDF = async (preguntaRespuestaAgrupada) => {
  if (!preguntaRespuestaAgrupada?.length) return null;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const logo = await loadImageAsBase64("/assets/images/logo.png");

  drawBackground(doc);
  drawHeader(doc, logo);

  const marginX = 15;
  const marginY = 34;
  const gap = 6;
  const qw = (PAGE_W - marginX * 2 - gap) / 2;
  const qh = (PAGE_H - marginY - 18 - gap) / 2;

  preguntaRespuestaAgrupada.slice(0, 4).forEach((item, qi) => {
    const col = qi % 2;
    const row = Math.floor(qi / 2);
    const qx = marginX + col * (qw + gap);
    const qy = marginY + row * (qh + gap);

    drawQuadrant(
      doc,
      qx,
      qy,
      qw,
      qh,
      qi + 1,
      item.pregunta,
      item.frases_seleccionadas || [],
      QCOLORS[qi % QCOLORS.length],
    );
  });

  drawFooter(doc);
  return doc;
};

export const downloadConfirmacionPDF = async (preguntaRespuestaAgrupada) => {
  const doc = await generateConfirmacionPDF(preguntaRespuestaAgrupada);
  if (!doc) return;
  const d = new Date();
  doc.save(`Mi-Rol-Vital_${d.toISOString().slice(0, 10)}.pdf`);
};

export const previewConfirmacionPDF = async (preguntaRespuestaAgrupada) => {
  const doc = await generateConfirmacionPDF(preguntaRespuestaAgrupada);
  if (!doc) return null;
  return doc.output("bloburl");
};
